import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { useFormContext, useWatch } from "react-hook-form";

import { FieldGroup } from "@/components/ui/field";
import {
  FormCombobox,
  FormInput,
  FormRecordMultiPicker,
  FormRecordPicker,
  FormSuggestChipList,
  LocationFields,
  SectionCard,
} from "components/BaseForm";
import { formatUserName, isUserActive } from "helpers/user";
import CaseService from "services/Case";
import EntityService from "services/Entity";
import ProductLineService from "services/ProductLine";
import SourceStatusService from "services/SourceStatus";
import UserService from "services/User";

const toOptions = records =>
  records.map(({ id, name }) => ({ key: id, value: id, text: name }));

const useOptions = (key, fetchRecords) =>
  useQuery({
    queryKey: ["case-form", key],
    queryFn: async () => toOptions(await fetchRecords()),
    initialData: [],
  }).data;

const fetchWebsites = async search => {
  const websites = await CaseService.getWebsitesForFilters({
    search_query: search,
  });
  return websites.map(({ name }) => name);
};

const fetchEntities = search =>
  EntityService.getEntitiesForFilters(search ? { search } : {});

const DetailsSection = ({ entityDisplay, investigatorsDisplay }) => {
  const { getValues } = useFormContext();
  const investigatorIds = useWatch({ name: "case_investigators" });
  const investigationStatuses = useOptions(
    "investigation-statuses",
    CaseService.getInvestigationStatusesForFilters
  );
  const sourceStatuses = useOptions(
    "source-statuses",
    SourceStatusService.getForFilters
  );
  const productLines = useOptions(
    "product-lines",
    ProductLineService.getForFilters
  );

  // Deactivated users are not offered, except those already assigned.
  const fetchInvestigators = async search => {
    const users = await UserService.getUsersForFilters(
      search ? { search_query: search } : {}
    );
    return users
      .filter(
        user =>
          user.id > 0 &&
          (investigatorIds.includes(user.id) || isUserActive(user))
      )
      .map(user => ({ ...user, name: formatUserName(user.name, user.status) }));
  };

  // A new entity takes the case's location, as in the legacy form.
  const createEntity = name =>
    EntityService.createEntity({
      name,
      region_id: getValues("region_id"),
      country: getValues("country"),
    });

  return (
    <SectionCard title="Details">
      <FieldGroup className="tw:grid tw:grid-cols-1 tw:gap-5 tw:md:grid-cols-2">
        <FormInput name="name" label="Name" required />
        <FormRecordPicker
          name="entity_id"
          label="Entity"
          fetchOptions={fetchEntities}
          onCreate={createEntity}
          placeholder="Search entities…"
          display={entityDisplay}
        />
        <FormRecordMultiPicker
          name="case_investigators"
          label="Investigators"
          placeholder="Pick Investigators"
          fetchOptions={fetchInvestigators}
          optionsKey="case-investigators"
          display={investigatorsDisplay}
        />
        <FormCombobox
          name="investigation_status_id"
          label="Investigation Status"
          placeholder="Pick an Investigation Status"
          options={investigationStatuses}
          clearable
        />
        <FormSuggestChipList
          name="website"
          label="Website"
          placeholder="Type a URL and press Enter"
          fetchSuggestions={fetchWebsites}
          suggestionsKey="case-websites"
        />
        <FormInput name="industry" label="Industry" />
        <FormInput name="company_description" label="Company Description" />
        <FormInput name="actionable_domain" label="Actionable Domain" />
        <LocationFields />
        <FormCombobox
          name="source_status_id"
          label="Source Status"
          placeholder="Pick a Source Status"
          options={sourceStatuses}
          clearable
        />
        <FormCombobox
          name="product_line_id"
          label="Product Line"
          required
          placeholder="Pick a Product Line"
          options={productLines}
        />
      </FieldGroup>
    </SectionCard>
  );
};

const displayShape = PropTypes.shape({
  id: PropTypes.number,
  label: PropTypes.string,
});

DetailsSection.propTypes = {
  entityDisplay: displayShape,
  investigatorsDisplay: PropTypes.arrayOf(displayShape),
};

export default DetailsSection;
